import { LoadingButton } from '@mui/lab'
import { Button, Stack, Typography } from '@mui/material'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { finalizeBillingProfile, setupBillingProfile } from 'api/subscription'
import StyledCardElement from 'components/form-elements/StyledCardElement'
import NotificationSys from 'components/NotificationSystem'
import { STRIPE_KEY } from 'const'
import { UIModal } from 'features/UI'
import { useMemo, useState } from 'react'

function InnerForm({ handleClose, onSuccess }: { handleClose: () => void; onSuccess: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)

  const handleSubmit = async () => {
    if (!stripe || !elements) return

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      NotificationSys.showWarning('Card details are not available. Please try again.')
      return
    }

    try {
      setLoading(true)

      // Step 1: Create Stripe Customer + SetupIntent
      const { data: setupData } = await setupBillingProfile()

      // Step 2: Confirm card via Stripe
      const { setupIntent, error } = await stripe.confirmCardSetup(setupData.clientSecret, {
        payment_method: { card: cardElement },
      })

      if (error) {
        NotificationSys.showWarning(
          <>
            Failed to add card.
            <br />
            {error.message}
          </>,
        )
        return
      }

      if (!setupIntent) {
        NotificationSys.showWarning('Failed to add card. Please try again.')
        return
      }

      // Step 3: Finalize — save card data in backend
      await finalizeBillingProfile(setupIntent.id)

      NotificationSys.showSuccess('Card added successfully')
      onSuccess()
    } catch {
      NotificationSys.showWarning('Failed to add card. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!stripe || !elements) {
    return <Typography>Loading...</Typography>
  }

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Enter your card details below. Your card will be saved for future payments.
      </Typography>

      <StyledCardElement />

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant="outlined" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={handleSubmit}
          disabled={!stripe || !elements}
        >
          Add Card
        </LoadingButton>
      </Stack>
    </Stack>
  )
}

export function AddCardModal({
  isOpen,
  handleClose,
  onSuccess,
}: {
  isOpen: boolean
  handleClose: () => void
  onSuccess: () => void
}) {
  const stripePromise = useMemo(() => loadStripe(STRIPE_KEY), [])

  return (
    <UIModal title="Add Payment Method" isOpen={isOpen} onClose={handleClose} width={480}>
      {isOpen && (
        <Elements stripe={stripePromise}>
          <InnerForm handleClose={handleClose} onSuccess={onSuccess} />
        </Elements>
      )}
    </UIModal>
  )
}
